import OpenInNew from '@mui/icons-material/OpenInNew'
import { Box, TextField, Typography } from '@mui/material'
import { GridActionsCellItem } from '@mui/x-data-grid-premium'
import { useState } from 'react'

export interface SpendControlRow {
  id: string
  name: string
  description?: string
  path: string[]
  modifiedAt: string
  order?: number
  categoryIds?: string[]
  virtual?: boolean
}

type Hook<Data, Payload = undefined> = (...args: Payload extends undefined ? [] : [Payload]) => {
  isLoading: boolean
  data: Data | undefined
}

export interface SpendControlTableProps {
  useRows: Hook<SpendControlRow[], { partner: string | undefined }>
  slots: {
    Table: DataTable
  }
  partner: string | undefined
  onEditRow?: (row: SpendControlRow) => void
}

type DataTableColumns = { field: string; [key: string]: unknown }[]

interface DataTableProps {
  rows: SpendControlRow[]
  columns: DataTableColumns
  loading: boolean | undefined
}

export type DataTable = React.ComponentType<DataTableProps>

const filterRows = (rows: SpendControlRow[], search: string): SpendControlRow[] => {
  if (!search.trim()) return rows
  const term = search.toLowerCase()
  const matchesSearch = (row: SpendControlRow) =>
    row.name.toLowerCase().includes(term) || (row.description?.toLowerCase().includes(term) ?? false)

  const matchingGroupPaths = new Set(rows.filter((r) => r.path.length === 1 && matchesSearch(r)).map((r) => r.path[0]))

  return rows.filter((row) => {
    if (row.path.length === 1) {
      return matchesSearch(row) || rows.some((r) => r.path.length === 2 && r.path[0] === row.path[0] && matchesSearch(r))
    }
    return matchesSearch(row) || matchingGroupPaths.has(row.path[0])
  })
}

export const SpendControlTable = ({ useRows, slots: { Table }, partner, onEditRow }: SpendControlTableProps) => {
  const { data = [], isLoading } = useRows({ partner })
  const [search, setSearch] = useState('')

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
      }}
    >
      <TextField label="Search" value={search} onChange={(e) => setSearch(e.target.value)} size="small" sx={{ maxWidth: 300 }} />
      <Table rows={filterRows(data, search)} columns={buildColumns(onEditRow)} loading={isLoading} />
    </Box>
  )
}

const formatDate = (value: string | undefined): string => {
  if (!value) return '—'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '—'
  return new Intl.DateTimeFormat('sv-SE', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).format(date)
}

const buildColumns = (onEditRow: ((row: SpendControlRow) => void) | undefined): DataTableColumns => [
  {
    headerName: 'Description',
    field: 'description',
    flex: 2,
    minWidth: 300,
    renderCell: ({ value }: { value?: string }) => (
      <Typography
        sx={{
          color: value ? 'text.primary' : 'text.disabled',
          fontSize: value != null ? 14 : 10,
          py: 1,
          whiteSpace: 'normal',
          wordBreak: 'break-word',
        }}
      >
        {value ?? '—'}
      </Typography>
    ),
  },
  {
    headerName: 'Order',
    field: 'order',
    width: 90,
    sortable: false,
    renderCell: ({ value }: { value?: number }) => (
      <Typography sx={{ color: value != null ? 'text.primary' : 'text.disabled', fontSize: value != null ? 14 : 10, py: 1 }}>
        {value ?? '—'}
      </Typography>
    ),
  },
  {
    headerName: 'Modified',
    field: 'modifiedAt',
    width: 165,
    renderCell: ({ value }: { value?: string }) => (
      <Typography sx={{ color: value ? 'text.primary' : 'text.disabled', fontSize: value ? 14 : 10, py: 1 }}>
        {formatDate(value)}
      </Typography>
    ),
  },
  {
    field: 'actions',
    type: 'actions',
    width: 64,
    getActions: ({ row }: { row: SpendControlRow }) =>
      onEditRow && !row.virtual
        ? [<GridActionsCellItem key="view" icon={<OpenInNew />} label="View" onClick={() => onEditRow(row)} />]
        : [],
  },
]
